// Pure dictation helpers (no browser APIs), so they can be unit-tested in node.
import type { Clarification, ParseContext } from '../parser';
import type { FinalResult } from './finalFilter';

/**
 * Keep the open questions short: drop "what about tooth X?" once X has an
 * entry, and never ask it twice. Other questions are always kept.
 */
export function mergeQuestions(
  open: Clarification[],
  incoming: Clarification[],
  recordedTeeth: ReadonlySet<number>
): Clarification[] {
  // Answered once every tooth it asked about has an entry.
  const answered = (q: Clarification) =>
    q.kind === 'missing' && !!q.teeth?.every((t) => recordedTeeth.has(t));
  // Only "what about tooth X" questions are interchangeable; two "which six?"
  // questions can carry different findings and must both stay.
  const sameMissing = (a: Clarification, b: Clarification) =>
    a.kind === 'missing' && b.kind === 'missing' && a.question === b.question;
  const kept = open.filter((q) => !answered(q));
  for (const q of incoming) {
    if (!kept.some((k) => sameMissing(k, q))) kept.push(q);
  }
  return kept;
}

type ChunkState = {
  current: ParseContext;
  /** Context before the last chunk. */
  previous: ParseContext;
  /** The last chunk only named a tooth and recorded nothing. */
  lastOnlyAwaited: boolean;
};

/**
 * What to parse for a speech result: nothing for a repeat; the whole text again
 * from before the last chunk when that chunk only named a tooth ("17" then
 * "17 18 hiányzik"); otherwise only the added words, so nothing is recorded twice.
 * `whole` is the full utterance: a price ("4500" + "dinara") spans the pieces.
 */
export function planSpeechChunk(
  f: FinalResult,
  s: ChunkState
): { text: string; from: ParseContext; whole: string } | null {
  if (f.kind === 'repeat') return null;
  if (f.kind === 'new') return { text: f.text, from: s.current, whole: f.text };
  if (s.lastOnlyAwaited) return { text: f.text, from: s.previous, whole: f.text };
  return { text: f.added, from: s.current, whole: f.text };
}

/**
 * The dictation's lines after a speech result: a repeat changes nothing, a
 * grown utterance replaces its shorter version, anything else is a new line.
 */
export function appendTranscriptLine(lines: readonly string[], f: FinalResult): string[] {
  if (f.kind === 'repeat') return [...lines];
  if (f.kind === 'extends' && lines.length) return [...lines.slice(0, -1), f.text];
  return [...lines, f.text];
}
